import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import ragIcon from '../../assets/icon/ragIcons.svg';
import backIcon from '../../assets/icon/backIcon.svg';
import resetIcon from '../../assets/icon/resetIcon.svg';
import tickIcon from '../../assets/icon/tickIcon.svg';
import deleteIcon from '../../assets/icon/deleteIcon1.svg';
import { getParam2, getParam3, getRagConfig, getRagList, proxyDatabricks, saveData, saveForm, updateEnabledField, updateRagConfig } from '../../service/ragApiServices';
import { useAzureData } from '../../context/AzureContext';
import SelectWithCheckbox from './SelectWithMultipleCheckbox';
import { fetchRagOboardingDetilas } from '../../service/apiServices';

const initialValues = {
  application_id: '',
  servicename: '',
  businessgroup: '',
  custombusinessgroup: '',
  functions: '',
  customfunctions: '',
  rag_app_name: '',
  source_doc_formats: [],
  data_load_frequency: '',
  data_load_day: '',
  embedding_model: '',
  chunk_type: '',
  vector_database: '',
  chunk_size: '',
  chunk_overlap: '',
  ai_enrichment_provider: '',
  ai_enrichment_list: [],
};

const datasource_initialValues = {
  dataSourceType: '',
  entity: '',
  source_url: '',
};

// Reusable Input Components
const TextField = ({
  label,
  name,
  value,
  required,
  disabled,
  placeholder,
  onChange,
  type
}: {
  label: string;
  name: string;
  value: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  type?: string;
  onChange: (key: any, value: string) => void;
}) => (
  <Form.Group controlId={name}>
    <Form.Label>
      {label} {required && <span className="text-danger">*</span>}
    </Form.Label>
    <Form.Control
      type={!type ? 'text' : type}
      placeholder={placeholder || label}
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      required={required}
      disabled={disabled}
      aria-required={required}
    />
  </Form.Group>
);

const SelectField = ({
  label,
  name,
  value,
  options,
  required,
  disabled,
  onChange
}: {
  label: string;
  name: string;
  value: string;
  options: { label: string; value: string }[];
  required?: boolean;
  disabled?: boolean;
  onChange: (key: any, value: string) => void;
}) => (
  <Form.Group controlId={name}>
    <Form.Label>
      {label} {required && <span className="text-danger">*</span>}
    </Form.Label>
    <Form.Select
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      required={required}
      disabled={disabled}
      aria-required={required}>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </Form.Select>
  </Form.Group>
);

const NewRagForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { apiToken, graphData } = useAzureData();
  const queryParams = new URLSearchParams(location.search);
  const query_rag_app_name = queryParams.get('a');
  const query_created_date_time = queryParams.get('t');
  let editRag = (query_rag_app_name && query_created_date_time) ? true : false
  const [formData, setFormData] = useState<any>(initialValues);
  const [dataSource, setDataSource] = useState<any>([
    {
      dataSourceType: '',
      entity: '',
      source_url: '',
    }
  ]);
  const [dataSourceType, setDataSourceType] = useState([]);
  const [showAdvanceSettings, setShowAdvanceSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [businessGroup, setBusinessGroup] = useState<any>([]);
  const [functions, setFunctions] = useState<any>([]);
  const [documentFormats, setDocumentFormats] = useState<any>([]);
  const [dataLoadFrequencies, setDataLoadFrequencies] = useState<any>(['Daily', 'Weekly', 'Manual']);

  const [embeddingModels, setEmbeddingModels] = useState<any>([]);
  //eslint-disable-next-line
  const [embeddingModelDimension, setEmbeddingModelDimension] = useState(0);
  const [chunkTypes, setChunkTypes] = useState<any>([]);
  const [vectorDatabases, setVectorDatabases] = useState<any>([]);
  const [enrichmentProviders, setEnrichmentProviders] = useState<any>([]);
  const [enrichments, setEnrichments] = useState<any>([]);
  const [ragOnboardingDetails, setRagOnboardingDetails] = useState<any>("")
  const [allRaglist, setAllRagList] = useState<any>("")
  const [error, setError] = useState(false);
  const [dataSourceErrors, setDataSourceErrors] = useState<any>({});


  const fetchDropdownData = async () => {
    try {
      const [param2Response, param3Response] = await Promise.all([
        getParam2(apiToken),
        getParam3(apiToken),
      ]);

      // @ts-ignore
      if (param2Response?.data?.status === 200) {
        // @ts-ignore
        setEmbeddingModels(param2Response?.data?.data);
        // @ts-ignore
        const dimension = param2Response?.data?.data[0]?.embedding_model_dimension;
        setEmbeddingModelDimension(dimension); // Store the dimension value
      }

      // @ts-ignore
      if (param3Response?.data?.status === 200) {
        // @ts-ignore
        const chunkData = param3Response?.data?.data[0];
        setBusinessGroup(chunkData["Business Group"].split(","));
        setFunctions(chunkData["Function"].split(","));
        setDocumentFormats(chunkData["Document Formats"].split(","));
        setDataLoadFrequencies(chunkData["Data Load Frequency"].split(","));
        setChunkTypes(chunkData["Chunking Type"].split(","));
        setVectorDatabases(
          chunkData["Vector Database"]
            .split(",")
            .map((label: any) => ({
              label: label.trim(),
              value: label.trim().toLowerCase().replace(/\s+/g, "_"),
            }))
        );
        setEnrichmentProviders(chunkData["Enrichment Provider"].split(","));
        setEnrichments(chunkData["Enrichment List"].split(","));
        setDataSourceType(chunkData["Data Source Types"].split(","));
      }

    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  }

  const fetchRagDetails = async () => {
    try {
      const response = await getRagConfig(apiToken, query_rag_app_name, query_created_date_time)
      // @ts-ignore
      const data = response?.data;
      if (data?.status === 200) {
        const multipleUrls:any = [];
        let entityMap:any = null
        if (data.data[0].entity) {
          entityMap = JSON.parse(data.data[0].entity);
          Object.keys(entityMap).forEach((key) => {
            const dataSet = entityMap[key];
            dataSet.values.forEach((val: any) => {
              multipleUrls.push({ dataSourceType: dataSet.sourceType, entity: val, source_url: key })
            })
          })
        }

        const fetchedData = {
          ...data.data[0],
          application_id: data.data[0].application_id,
          servicename: data.data[0].servicename,
          businessgroup: data.data[0].businessgroup,
          functions: data.data[0].function,
          rag_app_name: data.data[0].rag_app_name,
          source_doc_formats: data.data[0].source_doc_formats && data.data[0].source_doc_formats.split(","),
          data_load_frequency: data.data[0].data_load_frequency,
          data_load_day: data.data[0].data_load_day,
          embedding_model: data.data[0].embedding_model,
          chunk_type: data.data[0].chunk_type,
          vector_database: data.data[0].vector_database_provider,
          chunk_size: data.data[0].chunk_size,
          chunk_overlap: data.data[0].chunk_overlap,
          ai_enrichment_provider: data.data[0].ai_enrichment_provider,
          ai_enrichment_list: data.data[0].ai_enrichment_list && data.data[0].ai_enrichment_list.split(","),
          rag_app_status: "inprogress",
          dataSourceType: data.data[0].data_source_type,
          is_active: 0,
          rag_created_by: data.data[0].rag_created_by,
          rag_created_date_time: data.data[0].rag_created_date_time,
          rag_modfied_date_time: data.data[0].rag_modfied_date_time,
        };
        setFormData(fetchedData);
        setDataSource(multipleUrls)
        setShowAdvanceSettings(fetchedData?.embedding_model && true)
      }

    } catch (error) {
      console.error("Error while fetchingRagDetails:", error)
    }
  }

  const getRagOboardingDetails = async () => {
    let response = await fetchRagOboardingDetilas(apiToken) 
    setRagOnboardingDetails(response?.items)
  }

  const fetchRagLists = async () => {
    let response = await getRagList(apiToken)
    // @ts-ignore
    const ragAppNames = response?.data.data.map((item) => item.rag_app_name);
    setAllRagList(ragAppNames)
  }

  useEffect(() => {
    setFormData({
      ...formData,
      chunk_type: 'Markdown chunking',
      embedding_model: "text-embedding-3-large",
      vector_database: "azure_ai_search",
      chunk_size: 0,
      chunk_overlap: 0,
    });
    fetchDropdownData()
    fetchRagLists()
    getRagOboardingDetails()

    //eslint-disable-next-line
  }, [])

  useEffect(() => {
    editRag && fetchRagDetails()
    //eslint-disable-next-line
  }, [query_rag_app_name, query_created_date_time])
  
  useEffect(() => {
    if(formData?.application_id){
      let filteredData = ragOnboardingDetails?.length > 0 && ragOnboardingDetails?.filter((item:any) => item.application_id === formData.application_id)
      const mapData = {
        ...formData,
        servicename: filteredData[0]?.cmdb_servicename ?? "",
        businessgroup: filteredData[0]?.businessgroup ?? "",
        functions: filteredData[0]?.function ?? "",
      };
      setFormData(mapData);
    }
    //eslint-disable-next-line
  } ,[formData?.application_id])

  const handleAddDataSourceConfig = () => {
    setDataSource((prev: any) => [...prev, { ...datasource_initialValues }]);
  };

  const updateDataSourceConfig = (index: number, key: any, value: string | boolean) => {
    const updated = [...dataSource];

    if (!updated[index]) return;

    // @ts-ignore
    updated[index][key] = value;
    setDataSource(updated);

     // run validation when type or url changes
      if (key === "dataSourceType" || key === "source_url") {
        validateDataSource(index, updated[index]);
      }
  };

  const handleRemoveDataSourceConfig = (index: number) => {
    setDataSource((prev: any) => prev.filter((_: any, i: number) => i !== index));

    setDataSourceErrors((prev:any) => {
      const newErrors = { ...prev };
      delete newErrors[`sourceUrl_${index}`];
      return newErrors;
    });
  };

  const update = (key: any, value: string | boolean | number) => {
    if(key === "rag_app_name"){
      // @ts-ignore
      if(value && allRaglist?.includes(value.trim())){
        setError(true)
      }else{
        setError(false)
      }
    }
    setFormData((prev: any) => ({ ...prev, [key]: value }));
  };

  const updateSelect = (key: string, value: string[]) => {
    setFormData((prev: any) => ({ ...prev, [key]: value }));
  };

  const onReset = () => {
    setFormData(initialValues);
    setDataSource([datasource_initialValues]);
  };

  const validateDataSource = (index: number, item: any) => {
    const newErrors: any = { ...dataSourceErrors };
    const { dataSourceType, source_url } = item;
  
    if (
      dataSourceType === "URL" &&
      source_url.startsWith("https://unilever.sharepoint.com")
    ) {
      newErrors[`sourceUrl_${index}`] = "Invalid URL format";
    } else if (
      dataSourceType === "ADLS" &&
      !source_url.startsWith("https://dbstorage")
    ) {
      newErrors[`sourceUrl_${index}`] = "Invalid URL format for ADLS";
    } else if (
      dataSourceType === "Sharepoint" &&
      !source_url.startsWith("https://unilever.sharepoint.com")
    ) {
      newErrors[`sourceUrl_${index}`] = "Invalid URL format for Sharepoint";
    } else {
      delete newErrors[`sourceUrl_${index}`];
    }
  
    setDataSourceErrors(newErrors);
  };
  

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // if(error){
    //   alert("Please resolve all Errors")
    //   return
    // }

    // validate all rows before submit
    dataSource.forEach((item: any, index: number) => {
      validateDataSource(index, item);
    });

    if (Object.keys(dataSourceErrors).length > 0 || error) {
      alert("Please resolve all errors before submitting");
      return;
    }
    try {
      // First API call - saveForm
      const input1 = {
        application_id: formData.application_id,
        servicename: formData.servicename,
        businessgroup: formData.businessGroup === "Others" ? formData?.custombusinessgroup : formData.businessgroup,
        functions: formData.functions === "Others" ? formData?.customfunctions : formData.functions,
        rag_app_name: formData.rag_app_name,
        rag_app_status: "Inprogress",
        multipleurls: dataSource,
        chunk_type: formData.chunk_type,
        chunk_size: formData.chunk_size,
        chunk_overlap: formData.chunk_overlap,
        embedding_model: formData.embedding_model,
        ai_enrichment_list: (formData.ai_enrichment_provider && formData.ai_enrichment_provider !== "None") ? formData.ai_enrichment_list : [],
        ai_enrichment_provider: formData.ai_enrichment_provider,
        is_active: 1,
        created_by: graphData?.mail,
        vector_database: formData.vector_database,
        created_date_time: new Date(),
        modified_date_time: new Date(),
      }

      if (!editRag) {
        const response1 = await saveForm(apiToken, input1);

        // @ts-ignore
        if (response1.status === 200) {
          // Second API call - saveData
          const input2 = {
            application_id: formData.application_id,
            rag_app_name: formData.rag_app_name,
            data_load_frequency: formData.data_load_frequency,
            data_load_day: formData.data_load_day,
            source_doc_formats: formData.source_doc_formats,
            created_by: graphData?.mail,
            created_date_time: new Date(),
            datasourcetypes: dataSourceType,
            multipleurls: dataSource,
          };

          const response2 = await saveData(apiToken, input2)

          // @ts-ignore
          if (response2.status === 204) {
            throw new Error("saveData request returned no content (204).");
          }

          // @ts-ignore
          if (response2.status === 200) {
            // Check if the secret request is successful before proceeding
            // Make the second request to the databricks proxy
            const input3 = {
              application_id: formData.application_id,
              rag_app_name: formData.rag_app_name,
              rag_request_type: !editRag ? "New" : "Edit",
              servicename: formData.servicename,
              // source_url: dataSource?.map((item: any) => item.source_url).join(','),
              source_url: "", 
              multipleurls: dataSource,
              datasourcetype: dataSourceType
              // datasourcetype: dataSource?.map((item:any) => item.dataSourceType).join(',')
            }

            const response3 = await proxyDatabricks(apiToken, input3)

            // Check if the response from databricks proxy is valid
            // @ts-ignore
            if (response3.status === 204) {
              throw new Error("proxy/databricks request returned no content (204).");
            }
          }
        }
        navigate('/ragbuilder')

      } else {
        // First, update RAG config using the PUT request
        const response = await updateRagConfig(apiToken, formData.rag_app_name)
        // @ts-ignore
        if (response?.status === 200) {
          // Now, send the first POST request to saveForm
          await saveForm(apiToken, input1)

          const input2 = {
            application_id: formData.application_id,
            rag_app_name: formData.rag_app_name,
            data_load_frequency: formData.data_load_frequency,
            data_load_day: formData.data_load_day ? formData.data_load_day : "",
          }
          await updateEnabledField(apiToken, input2);

          const input3 = {
            application_id: formData.application_id,
            rag_app_name: formData.ragAppName,
            rag_request_type: "Edit",
            servicename: formData.servicename,
            // source_url: dataSource?.map((item: any) => item.source_url).join(','),
            source_url: "",     
            multipleurls: dataSource,
            data_source_type: dataSource?.map((item: any) => item.dataSourceType).join(','),
            is_old_rag_app: formData.entity ? false : true
          }
          // Send the  POST request to databricks proxy
          await proxyDatabricks(apiToken, input3);

        }
        navigate('/ragbuilder')
      }

    } catch (error) {
      console.error('Error: ', error);
    }
  }

  const showChunkFields = [
    "Fixed-size chunking",
    "Format-specific chunking",
    "Token-size chunking"
  ].includes(formData?.chunk_type);

  return (
    <div className="mt-4 ps-4 pe-4 mb-4 modal_onBoarding_parent">
      <div className="mb-4">
        <button
          onClick={() => navigate('/ragbuilder')}
          className="btn btn-link text-muted p-0 mb-3 text-decoration-none back_btn">
          <img src={backIcon} alt="back" height="14px" />
          Back
        </button>

        <div className="d-flex align-items-center mb-2">
          <img src={ragIcon} alt="onboarding" className="me-2" />
          <h6 className="h4 mb-0 onboarding_heading">Create new RAG</h6>
        </div>

        <p className="text-muted small mb-0 onboarding_subheading">
          Create new RAG App and to the list
        </p>
      </div>
      <Form onSubmit={onSubmit}>
        <section className="mt-3">
          <h6 className="mb-1">RAG Details</h6>
          <div className="text-muted mb-3">Core setup of your RAG App.</div>

          <Row className="g-3">
            <Col md={6} lg={4}>
              <SelectField
                label="Application Name"
                name="application_id"
                value={formData.application_id}
                disabled={editRag}
                options={[
                  { label: 'Select Application Name', value: '' },
                  ...(ragOnboardingDetails && ragOnboardingDetails?.length > 1
                    ? ragOnboardingDetails?.map((item: any) => ({
                        label: item.application_name,
                        value: item.application_id
                      }))
                    : [])
                ]}
                onChange={update}
              />
            </Col>
            <Col md={6} lg={4}>
              <TextField
                label="Service Name"
                name="servicename"
                value={formData.servicename}
                required
                disabled={true}
                onChange={update}
              />
            </Col>
            <Col md={6} lg={4}>
              {editRag ?
                <TextField
                  label="Business Group"
                  name="businessgroup"
                  value={formData.businessgroup}
                  disabled={true}
                  required
                  onChange={update}
                /> :
                <SelectField
                  label="Business Group"
                  name="businessgroup"
                  value={formData.businessgroup}
                  disabled={true}
                  options={[
                    { label: 'Select Business Group', value: '' },
                    ...businessGroup?.map((data: any) => ({
                      label: data,
                      value: data
                    }))
                  ]}
                  onChange={update}
                />

              }
            </Col>
            {formData?.businessgroup === "Others" &&
              <Col md={6} lg={4}>
                <TextField
                  label="Custom Business Group"
                  name="custombusinessgroup"
                  value={formData.custombusinessgroup}
                  required
                  onChange={update}
                />
              </Col>
            }

            <Col md={6} lg={4}>
              {editRag ?
                <TextField
                  label="Function"
                  name="functions"
                  value={formData.functions}
                  disabled={true}
                  required
                  onChange={update}
                /> :
                <SelectField
                  label="Function"
                  name="functions"
                  value={formData.functions}
                  disabled={true}
                  options={[
                    { label: 'Select Function', value: '' },
                    ...functions?.map((data: any) => ({
                      label: data,
                      value: data
                    }))
                  ]}
                  onChange={update}
                />
              }
            </Col>
            {formData?.functions === "Others" &&
              <Col md={6} lg={4}>
                <TextField
                  label="Custom Funtion"
                  name="customfunctions"
                  value={formData.customfunctions}
                  required
                  onChange={update}
                />
              </Col>
            }

            <Col md={6} lg={4}>
              <TextField
                label="RAG App Name"
                name="rag_app_name"
                value={formData.rag_app_name}
                required
                disabled={editRag}
                onChange={update}
              />
              {error &&
                <div className="text-danger">
                  RAG with this name already exists. Please use a different name.
                </div>
              }
            </Col>
            <Col md={6} lg={4}>
              {/* <SelectField
                label="Document Formats"
                name="source_doc_formats"
                value={formData.source_doc_formats}
                required
                disabled={editRag}
                options={[
                  { label: 'Select Document Format', value: '' },
                  ...documentFormats?.map((data: any) => ({
                    label: data,
                    value: data
                  }))
                ]}
                onChange={update}
              /> */}
              <SelectWithCheckbox
                label="Document Formats"
                name="source_doc_formats"
                value={formData.source_doc_formats}
                required
                disabled={editRag}
                options={[
                  ...documentFormats.map((item: string) => ({
                    label: item,
                    value: item
                  }))
                ]}
                onChange={updateSelect}
              />
            </Col>

          </Row>
        </section>

        {/* Data source */}
        <section className="mt-4">
          <div className="d-flex justify-content-between">
            <div>
              <h6 className="mb-1">Data Source</h6>
              <div className="text-muted mb-3">Add data sources to your RAG.</div>
            </div>
            {
              !editRag &&
              <div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    handleAddDataSourceConfig()
                  }}>
                  + Add Data Source
                </Button>
              </div>
            }
          </div>

          <Row className="g-3 model_specific_content_container"
            style={{ height: dataSource.length > 1 ? '200px' : '', overflowY: 'auto' }}>
            {dataSource?.map((config: any, index: number) => (
              <React.Fragment key={index}>
                <Col md={dataSource.length > 1 ? 11 : 12}>
                  <div className={`${dataSource.length > 1 && 'mb-2'}`} key={index}>
                    <Row className="g-3">
                      <Col md={6} lg={4}>
                        {/* <TextField
                          label="Data Source Type"
                          name="dataSourceType"
                          value={config.dataSourceType}
                          required
                          onChange={(key, val) => updateDataSourceConfig(index, key, val)}
                        /> */}
                        <SelectField
                          label="Data Source Type"
                          name="dataSourceType"
                          value={config.dataSourceType}
                          disabled={editRag}
                          options={[
                            { label: 'Select Data Source Type', value: '' },
                            ...dataSourceType?.map((data: any) => ({
                              label: data,
                              value: data
                            }))
                          ]}
                          onChange={(key, val) => updateDataSourceConfig(index, key, val)}
                        />

                      </Col>
                      <Col md={6} lg={4}>
                        <TextField
                          label="Enter Entity"
                          name="entity"
                          value={config.entity}
                          required
                          disabled={editRag}
                          onChange={(key, val) => updateDataSourceConfig(index, key, val)}
                        />
                      </Col>
                      <Col md={6} lg={4}>
                        <TextField
                          label="Enter URL"
                          name="source_url"
                          value={config.source_url}
                          required
                          disabled={editRag}
                          onChange={(key, val) => updateDataSourceConfig(index, key, val)}
                        />
                          {dataSourceErrors[`sourceUrl_${index}`] && (
                            <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                              {dataSourceErrors[`sourceUrl_${index}`]}
                            </div>
                          )}
                      </Col>
                    </Row>
                  </div>
                </Col>
                {dataSource.length > 1 && (
                  <Col md={1} className="d-flex">
                    <button
                      type="button"
                      onClick={() => handleRemoveDataSourceConfig(index)}
                      className="btn btn-sm">
                      <img src={deleteIcon} alt="delete" />
                    </button>
                  </Col>
                )}
              </React.Fragment>
            ))}
          </Row>
        </section>

        <section className="mt-4">
          <Row className="g-3">
            <Col md={6} lg={4}>
              <SelectField
                label="Data Load Frequency"
                name="data_load_frequency"
                value={formData.data_load_frequency}
                required
                options={[
                  { label: 'Select Data Load Frequency', value: '' },
                  ...dataLoadFrequencies?.map((data: any) => ({
                    label: data,
                    value: data
                  }))
                ]}
                onChange={update}
              />
            </Col>
            {editRag &&
              <Col md={6} lg={4}>
                <SelectField
                  label="Data Refresh Day"
                  name="data_load_day"
                  value={formData.data_load_day}
                  required
                  options={[
                    { label: 'Select Load Day', value: '' },
                    { label: 'Sunday', value: 'Sunday' },
                    { label: 'Monday', value: 'Monday' },
                    { label: 'Tuesday', value: 'Tuesday' },
                    { label: 'Wednesday', value: 'Wednesday' },
                    { label: 'Thursday', value: 'Thursday' },
                    { label: 'Friday', value: 'Friday' },
                    { label: 'Saturday', value: 'Saturday' }
                  ]}
                  onChange={update}
                />
              </Col>

            }

          </Row>
        </section>

        <section className="mt-4">
          <Form.Check
            type="switch"
            id="rag-switch"
            label="Advanced Settings"
            className='rag_switch mb-3'
            checked={showAdvanceSettings}
            onChange={(e) => {
              setShowAdvanceSettings(e.target.checked)
            }}
          />

          {showAdvanceSettings && <>
            <Row className="g-3">
              <Col md={6} lg={4}>
                <SelectField
                  label="Embedding Model"
                  name="embedding_model"
                  value={formData.embedding_model}
                  required
                  options={[
                    { label: "Select Embedding Model", value: "" },
                    ...embeddingModels?.map((modelObj: any) => ({
                      label: `${modelObj.embedding_model} (${modelObj.embedding_model_dimension})`,
                      value: modelObj.embedding_model
                    }))
                  ]}
                  onChange={update}
                />
              </Col>
              <Col md={6} lg={4}>
                <SelectField
                  label="Chunk Type"
                  name="chunk_type"
                  value={formData.chunk_type}
                  required
                  options={[
                    { label: 'Select Chunk Type', value: '' },
                    ...chunkTypes?.map((data: any) => ({
                      label: data,
                      value: data
                    }))
                  ]}
                  onChange={update}
                />
              </Col>
              <Col md={6} lg={4}>
                <SelectField
                  label="Vector Database Provider"
                  name="vector_database"
                  value={formData.vector_database}
                  required
                  disabled={editRag}
                  options={[
                    { label: 'Select Database Provider', value: '' },
                    ...vectorDatabases?.map((db: any) => ({
                      label: db.value,
                      value: db.value
                    }))
                  ]}
                  onChange={update}
                />
              </Col>
              {showChunkFields &&
                <>
                  <Col md={6} lg={4}>
                    <TextField
                      label="Chunk Size (Between 0 - 3000)"
                      name="chunk_size"
                      value={formData.chunk_size}
                      disabled={editRag}
                      onChange={update}
                    />
                  </Col>
                  <Col md={6} lg={4}>
                    <TextField
                      label="Chunk Overlap (Between 0 - 8000)"
                      name="chunk_overlap"
                      value={formData.chunk_overlap}
                      disabled={editRag}
                      onChange={update}
                    />
                  </Col>
                </>
              }
              <Col md={6} lg={4}>
                <SelectField
                  label="Enrichment Provider"
                  name="ai_enrichment_provider"
                  value={formData.ai_enrichment_provider}
                  options={[
                    { label: 'Select Enrichment Provider', value: '' },
                    ...enrichmentProviders?.map((data: any) => ({
                      label: data,
                      value: data
                    }))
                  ]}
                  onChange={update}
                />
              </Col>
              {formData.ai_enrichment_provider && formData.ai_enrichment_provider.toLowerCase() !== "none" && (
                <Col md={6} lg={4}>
                  <SelectWithCheckbox
                    label="AI Enrichment List"
                    name="ai_enrichment_list"
                    value={formData.ai_enrichment_list ? formData.ai_enrichment_list : []}
                    // disabled={!!editModelId}
                    required
                    options={[
                      ...enrichments.map((item: string) => ({
                        label: item,
                        value: item
                      }))
                    ]}
                    onChange={updateSelect}
                  />
                </Col>
              )
              }
            </Row>
          </>
          }
        </section>

        {/* Actions */}
        <Row className="mt-4">
          <Col className="d-flex justify-content-end gap-2">
            {!editRag && (
              <Button className="onboarding_resetBtn" type="button" onClick={onReset}>
                <img src={resetIcon} alt="reset" className="me-1" />
                Reset Form
              </Button>
            )}
            {loading ? (
              <Button variant="primary" type="submit">
                Create <Spinner animation="border" size="sm" />
              </Button>
            ) : (
              <Button variant="primary" type="submit">
                <img src={tickIcon} alt="submit" className="me-1" />
                {!editRag ? 'Apply' : 'Update'}
              </Button>
            )}
          </Col>
        </Row>
      </Form>

    </div>
  )
}

export default NewRagForm